'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { ImageType } from '@eliadmin/core/src/api/api-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';
import { CustomBreadcrumbs } from '../ui/custom-breadcrumbs';
import { Heading } from '../ui/heading';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { MdKeyboardDoubleArrowRight } from 'react-icons/md';

export type Category = {
  data?: Category[];
  childs: Category[];
  name: string;
  description: string;
  short_description: string;
  slug: string;
  icon?: string;
  image?: ImageType;
};

type CategoriesPageNewProps = {
  categories: Category[];
};

export const CategoriesPageNew = ({ categories }: CategoriesPageNewProps) => {
  const { t } = useTranslations();

  const cardClassName =
    'w-full col-span-1 relative flex flex-col items-end justify-end p-4 sm:p-5 lg:p-6';

  return (
    <section className={cn('custom-section', 'gap-8 py-8 sm:gap-10 sm:py-24 lg:gap-12 lg:py-32')}>
      <div
        className={cn(
          'custom-container',
          'flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6',
        )}
      >
        <CustomBreadcrumbs breadcrumbs={[{ name: t('text176'), href: ROUTES.CATEGORIES() }]} />
        <Heading title={t('text176')} tag="h1" />
      </div>
      <div className={cn('custom-container', 'grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6')}>
        <Link
          prefetch={false}
          href={ROUTES.CATEGORY({ slug: categories[0]?.slug })}
          className={cn(cardClassName, 'aspect-square sm:aspect-auto sm:h-auto')}
        >
          <Image
            src={categories[0]?.image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(categories?.[0]?.image, categories?.[0]?.name || 'Category Image')}
            width={0}
            height={0}
            className="absolute inset-0 z-0 h-full w-full object-cover object-center"
            sizes="100vw"
          />
          <div className="relative z-10 flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            <Heading title={categories[0]?.name} tag="h2" className={{ title: 'text-white' }} />
            <div className="flex items-center justify-start gap-1 text-sm font-medium uppercase text-white">
              {t('text172')} <MdKeyboardDoubleArrowRight size={20} className="text-white" />
            </div>
          </div>
          <div className={cn('ea-categories__category-card-overlay')} />
        </Link>
        <div className="col-span-1 grid w-full grid-cols-1 gap-4 md:gap-6">
          {categories.slice(1, 3).map(category => (
            <Link
              key={category.slug}
              prefetch={false}
              href={ROUTES.CATEGORY({ slug: category.slug })}
              className={cn(cardClassName, 'h-[172px] sm:h-[200px] md:h-[250px] lg:h-[282px]')}
            >
              <Image
                src={category?.image?.url ?? '/images/placeholder.png'}
                alt={resolveImageAlt(category?.image, category?.name, category?.name || 'Category Image')}
                width={0}
                height={0}
                className="absolute inset-0 z-0 h-full w-full object-cover object-center"
                sizes="100vw"
              />
              <div className="relative z-10 flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
                <Heading title={category?.name} tag="h2" className={{ title: 'text-white' }} />
                <div className="flex items-center justify-start gap-1 text-sm font-medium uppercase text-white">
                  {t('text172')} <MdKeyboardDoubleArrowRight size={20} className="text-white" />
                </div>
              </div>
              <div className={cn('ea-categories__category-card-overlay')} />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
